import { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputDate, formatCurrency, getDate } from "medere-ui-framework";
import { CompleteBudgetDetailDTO } from "../../../dto/budget/BudgetDetailDTO";
import { getBudgetDetailByPatient } from "../../../servicesHooks/BillHospitalization/useBillHospitalizationService";
import { amountOf, BILL_ITEM_CATEGORIES, BILL_ITEM_CATEGORY_CONFIG, DETAIL_TYPES, formatUnits, toUnits } from "./billItems";
import { formatApplicationDate } from "./tableParts";

interface AddPeriodItemsDialogProps {
    visible: boolean;
    onHide: () => void;
    patient: number;
    billingEntity: number | null | undefined;
    /** Período con el que abre (el de la factura); se puede cambiar y volver a buscar. */
    initialFrom: Date;
    initialTo: Date;
    onAdd: (items: CompleteBudgetDetailDTO[]) => void;
}

type Row = CompleteBudgetDetailDTO & { rowKey: string; applicationdate?: string | null };

/** Nombre de la categoría de un detalle del backend (Equipamiento no se factura en esta pantalla). */
const categoryTitle = (type: bigint | null): string => {
    const category = BILL_ITEM_CATEGORIES.find(c => DETAIL_TYPES[c].includes(Number(type)));
    if (category) return BILL_ITEM_CATEGORY_CONFIG[category].title;
    return Number(type) === 4 ? 'Equipamiento' : 'Otro';
};

/**
 * "Agregar ítems del período" (edición): se elige el período, se buscan los ítems del paciente en ese rango y se
 * agregan los seleccionados a la factura, sin reemplazar los que ya tiene.
 */
export default function AddPeriodItemsDialog({ visible, onHide, patient, billingEntity, initialFrom, initialTo, onAdd }: AddPeriodItemsDialogProps) {
    const [from, setFrom] = useState<Date>(initialFrom);
    const [to, setTo] = useState<Date>(initialTo);
    const [rows, setRows] = useState<Row[]>([]);
    const [selected, setSelected] = useState<Row[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [selectionError, setSelectionError] = useState<string | null>(null);
    const [rangeError, setRangeError] = useState<string | null>(null);
    const requestRef = useRef(0);

    async function search(rangeFrom: Date, rangeTo: Date) {
        if (rangeFrom > rangeTo) {
            setRangeError('La fecha hasta tiene que ser posterior a la fecha desde');
            return;
        }
        setRangeError(null);
        const request = ++requestRef.current;
        setLoading(true);
        setLoadError(null);
        setSelected([]);
        try {
            const details: CompleteBudgetDetailDTO[] = await getBudgetDetailByPatient(patient, rangeFrom, rangeTo, billingEntity);
            if (request !== requestRef.current) return;
            setRows((Array.isArray(details) ? details : []).map((d, i) => ({ ...d, rowKey: `${i}-${d.itemid ?? d.description}` })));
        } catch {
            if (request === requestRef.current) setLoadError('No se pudieron traer los ítems del período. Intentá de nuevo.');
        } finally {
            if (request === requestRef.current) setLoading(false);
        }
    }

    // Al abrir, busca con el período de la factura.
    useEffect(() => {
        if (!visible) return;
        setFrom(initialFrom);
        setTo(initialTo);
        setSelectionError(null);
        void search(initialFrom, initialTo);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible]);

    const add = () => {
        if (selected.length === 0) {
            setSelectionError('Elegí al menos un ítem');
            return;
        }
        // Se devuelven los detalles tal como llegan del backend (sin rowKey).
        onAdd(selected.map(({ rowKey: _rowKey, ...detail }) => detail as CompleteBudgetDetailDTO));
        onHide();
    };

    const footer = (
        <>
            <Button type="button" className="buttonMain2" outlined label="Cancelar" onClick={onHide} />
            <Button type="button" className="buttonMain" label="Agregar ítems" onClick={add} />
        </>
    );

    return (
        <Dialog
            visible={visible}
            onHide={onHide}
            header="Agregar ítems del período"
            footer={footer}
            className="nv-scope nv-dialog"
            modal
            draggable={false}
        >
            <div className="nv-dialog-body">
                <div className="nv-form-row nv-align-end">
                    <InputDate
                        label="Período desde"
                        className="nv-w-s"
                        value={getDate(from)}
                        onChange={(e: { value?: Date | null }) => { if (e.value) setFrom(e.value); }}
                        error={Boolean(rangeError)}
                    />
                    <InputDate
                        label="Período hasta"
                        className="nv-w-s"
                        value={getDate(to)}
                        onChange={(e: { value?: Date | null }) => { if (e.value) setTo(e.value); }}
                        error={Boolean(rangeError)}
                        caption={rangeError ?? undefined}
                    />
                    <Button type="button" className="buttonMain3" icon="pi pi-search" label="Buscar" onClick={() => { void search(from, to); }} />
                </div>

                <div className="nv-stack-4">
                    <span className="nv-muted">
                        {loading ? 'Buscando…' : `${rows.length} ${rows.length === 1 ? 'ítem' : 'ítems'} en el período · ${selected.length} ${selected.length === 1 ? 'seleccionado' : 'seleccionados'}`}
                    </span>
                    {selectionError && (
                        <small className="p-error" role="alert"><i className="pi pi-exclamation-triangle" aria-hidden /> {selectionError}</small>
                    )}
                </div>

                <DataTable
                    value={rows}
                    dataKey="rowKey"
                    selectionMode="checkbox"
                    selection={selected}
                    onSelectionChange={(e) => { setSelected(e.value as Row[]); setSelectionError(null); }}
                    scrollable
                    scrollHeight="440px"
                    loading={loading}
                    emptyMessage={loadError ?? 'No hay ítems del paciente en este período'}
                    className="nv-load-table"
                >
                    <Column selectionMode="multiple" headerClassName="nv-col-check" className="nv-col-check" />
                    <Column header="Tipo" className="nv-col-status" body={(row: Row) => categoryTitle(row.type)} />
                    <Column header="Descripción" className="nv-col-item" body={(row: Row) => <span className="nv-truncate nv-tip" data-pr-tooltip={row.description ?? ''}>{row.description}</span>} />
                    <Column header="Fecha" align="center" alignHeader="center" className="nv-col-date" body={(row: Row) => formatApplicationDate(row.applicationdate)} />
                    <Column header="Unidades" align="center" alignHeader="center" className="nv-col-qty" body={(row: Row) => formatUnits(toUnits(row.unitsqty))} />
                    <Column header="Valor unitario" align="right" alignHeader="right" className="nv-col-money" body={(row: Row) => formatCurrency(Number(row.unitprice) || 0)} />
                    <Column header="Total" align="right" alignHeader="right" className="nv-col-money" body={(row: Row) => <span className="nv-strong">{formatCurrency(amountOf(toUnits(row.unitsqty), Number(row.unitprice) || 0).toNumber())}</span>} />
                </DataTable>
            </div>
        </Dialog>
    );
}
